import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSearchLoading } from '@shared/hooks/useSearchLoading';
import type { FilterOption } from '@shared/components/FilterPopup';
import type { DateRange } from '@shared/components/FilterPopup/types';
import { listMyServiceRequestsApiService } from '../../api/listMyServiceRequestsApiService';
import type {
  ServiceRequestListItem,
  ServiceRequestStatus,
} from '../../types/serviceRequestListType';

const ALLOWED_LIMITS = [10, 30, 50, 100] as const;

const DEFAULT_STATUS_OPTIONS: FilterOption[] = [
  { label: 'Oczekuje', value: 'pending', checked: false },
  { label: 'W trakcie weryfikacji', value: 'in_review', checked: false },
  { label: 'Przydzielone', value: 'assigned', checked: false },
  { label: 'Zaakceptowane', value: 'accepted', checked: false },
  { label: 'Odrzucone', value: 'rejected', checked: false },
  { label: 'W realizacji', value: 'in_progress', checked: false },
  { label: 'Opóźnione', value: 'delayed', checked: false },
  { label: 'Zakończone', value: 'completed', checked: false },
  { label: 'Zamknięte', value: 'closed', checked: false },
];

const VALID_STATUSES = DEFAULT_STATUS_OPTIONS.map(
  (o) => o.value
) as ServiceRequestStatus[];

const isValidStatus = (s: string): boolean =>
  (VALID_STATUSES as string[]).includes(s);

const isValidSortOrder = (s: string): s is 'asc' | 'desc' =>
  s === 'asc' || s === 'desc';

const sanitizeStatusList = (values: string[]): string =>
  values.filter(isValidStatus).join(',');

const statusListFromParam = (raw: string | null): string[] => {
  if (!raw) {
    return [];
  }
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(isValidStatus);
};

const buildStatusOptions = (selected: string[]): FilterOption[] =>
  DEFAULT_STATUS_OPTIONS.map((o) => ({
    ...o,
    checked: selected.includes(o.value),
  }));

const sameSelectedStatuses = (a: FilterOption[], b: FilterOption[]): boolean =>
  a.every((opt, i) => opt.checked === b[i]?.checked);

export interface UseMyRequestsListResult {
  items: ServiceRequestListItem[];
  loading: boolean;
  page: number;
  setPage: (page: number) => void;
  limit: number;
  totalCount: number;
  search: string;
  setSearch: (search: string) => void;
  sortColumn: string | null;
  sortOrder: 'asc' | 'desc';
  handleSort: (columnKey: string) => void;
  statusOptions: FilterOption[];
  setStatusOptions: (options: FilterOption[]) => void;
  dateRange: DateRange;
  setDateRange: (range: DateRange) => void;
  appliedFilters: { statusOptions: FilterOption[]; dateRange: DateRange };
  handleApplyFilters: () => void;
  handleClearFilters: () => void;
  limitError: string;
  handleLimitChange: (limit: number) => void;
  hasNoData: boolean;
  isSearchClearing: boolean;
}

export function useMyRequestsList(): UseMyRequestsListResult {
  const [searchParams, setSearchParams] = useSearchParams();
  const isSyncingFromUrl = useRef(false);

  // ---- Initial state from URL (validated) ----
  const initial = (() => {
    const urlPage = searchParams.get('page');
    const urlLimit = searchParams.get('limit');
    const urlSearch = (searchParams.get('search') ?? '').trim();
    const urlSortOrder = searchParams.get('sortOrder');
    const urlStatusList = statusListFromParam(searchParams.get('status'));

    const pageNum = urlPage ? parseInt(urlPage, 10) : 1;
    const limitNum = urlLimit ? parseInt(urlLimit, 10) : 10;

    return {
      page: !isNaN(pageNum) && pageNum >= 1 ? pageNum : 1,
      limit: (ALLOWED_LIMITS as readonly number[]).includes(limitNum)
        ? limitNum
        : 10,
      search: urlSearch.length > 200 ? urlSearch.slice(0, 200) : urlSearch,
      sortOrder:
        urlSortOrder && isValidSortOrder(urlSortOrder) ? urlSortOrder : 'desc',
      statusOptions: buildStatusOptions(urlStatusList),
    };
  })();

  const {
    search,
    debouncedSearch,
    setSearch: setSearchWithLoad,
    setSearchDirect: setSearchState,
    loading,
    setLoading,
    effectiveLoading,
  } = useSearchLoading(initial.search, 500);

  const [items, setItems] = useState<ServiceRequestListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);

  const [page, setPage] = useState(initial.page);
  const [limit, setLimit] = useState(initial.limit);
  const [sortColumn] = useState<string | null>('submitted_on');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(initial.sortOrder);
  const [limitError, setLimitError] = useState('');

  const [statusOptions, setStatusOptions] = useState<FilterOption[]>(
    initial.statusOptions
  );
  const [dateRange, setDateRange] = useState<DateRange>({});
  const [appliedFilters, setAppliedFilters] = useState<{
    statusOptions: FilterOption[];
    dateRange: DateRange;
  }>({
    statusOptions: initial.statusOptions,
    dateRange: {},
  });

  const hasFetchedRef = useRef(false);
  const prevDebouncedSearchRef = useRef<string>(debouncedSearch);

  // -------------------------------------------------------------------------
  // URL → state (browser back/forward)
  // -------------------------------------------------------------------------
  useEffect(() => {
    isSyncingFromUrl.current = true;

    const urlPage = searchParams.get('page');
    const urlLimit = searchParams.get('limit');
    const urlSearch = (searchParams.get('search') ?? '').trim();
    const urlSortOrder = searchParams.get('sortOrder');
    const urlStatusList = statusListFromParam(searchParams.get('status'));

    const nextPage = (() => {
      const v = urlPage ? parseInt(urlPage, 10) : 1;
      return !isNaN(v) && v >= 1 ? v : 1;
    })();
    if (nextPage !== page) {
      setPage(nextPage);
    }

    const nextLimit = (() => {
      const v = urlLimit ? parseInt(urlLimit, 10) : 10;
      return (ALLOWED_LIMITS as readonly number[]).includes(v) ? v : 10;
    })();
    if (nextLimit !== limit) {
      setLimit(nextLimit);
    }

    const nextSearch =
      urlSearch.length > 200 ? urlSearch.slice(0, 200) : urlSearch;
    if (nextSearch !== search) {
      setSearchState(nextSearch);
      // Show shimmer immediately so the empty-state never flashes during the
      // 500ms debounce window (e.g. sidenav reset clears search via URL).
      setLoading(true);
    }

    const nextSortOrder: 'asc' | 'desc' =
      urlSortOrder && isValidSortOrder(urlSortOrder) ? urlSortOrder : 'desc';
    if (nextSortOrder !== sortOrder) {
      setSortOrder(nextSortOrder);
    }

    const nextStatusOptions = buildStatusOptions(urlStatusList);
    if (!sameSelectedStatuses(nextStatusOptions, statusOptions)) {
      setStatusOptions(nextStatusOptions);
    }
    if (
      !sameSelectedStatuses(nextStatusOptions, appliedFilters.statusOptions)
    ) {
      setAppliedFilters((prev) => ({
        ...prev,
        statusOptions: nextStatusOptions,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Reset isSyncingFromUrl on the next state-change tick so the state→URL
  // effect skips its first run after a URL-driven sync.
  useEffect(() => {
    if (isSyncingFromUrl.current) {
      isSyncingFromUrl.current = false;
    }
  }, [page, limit, search, sortOrder, appliedFilters]);

  // -------------------------------------------------------------------------
  // state → URL
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (isSyncingFromUrl.current) {
      return;
    }

    // Only write My Requests params when this tab is active. If the user has
    // switched to another tab, the component may still be mounted but must not
    // push its own params back into the URL.
    const currentTab = searchParams.get('tab');
    if (currentTab !== null && currentTab !== 'my-requests') {
      return;
    }

    const params = new URLSearchParams();

    // Preserve params owned by other hooks/components so this effect does not
    // clobber them when it rewrites the URL.
    const tab = searchParams.get('tab');
    if (tab) {
      params.set('tab', tab);
    }
    const cols = searchParams.get('cols');
    if (cols) {
      params.set('cols', cols);
    }

    if (page !== 1) {
      params.set('page', page.toString());
    }
    if (limit !== 10) {
      params.set('limit', limit.toString());
    }
    const trimmedSearch = search.trim();
    if (trimmedSearch) {
      params.set('search', trimmedSearch);
    }
    const selectedStatuses = sanitizeStatusList(
      appliedFilters.statusOptions.filter((o) => o.checked).map((o) => o.value)
    );
    if (selectedStatuses) {
      params.set('status', selectedStatuses);
    }
    params.set('sortOrder', sortOrder);

    if (params.toString() !== searchParams.toString()) {
      setSearchParams(params, { replace: false });
    }
  }, [
    page,
    limit,
    search,
    sortOrder,
    appliedFilters,
    searchParams,
    setSearchParams,
  ]);

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------
  const handleSort = useCallback((columnKey: string) => {
    if (columnKey !== 'submitted_on') {
      return;
    }
    setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    setPage(1);
  }, []);

  const handleLimitChange = useCallback((newLimit: number) => {
    if (!(ALLOWED_LIMITS as readonly number[]).includes(newLimit)) {
      setLimitError('Allowed values: 10, 30, 50, 100.');
      return;
    }
    setLimitError('');
    setLimit(newLimit);
    setPage(1);
  }, []);

  const handleApplyFilters = useCallback(() => {
    setAppliedFilters({ statusOptions, dateRange });
    setPage(1);
  }, [statusOptions, dateRange]);

  const handleClearFilters = useCallback(() => {
    const hasAppliedFilters =
      appliedFilters.statusOptions.some((o) => o.checked) ||
      !!appliedFilters.dateRange.startDate ||
      !!appliedFilters.dateRange.endDate;

    // Always reset the staged selections shown in the popup
    setStatusOptions([...DEFAULT_STATUS_OPTIONS]);
    setDateRange({});

    // Only push to appliedFilters (and trigger a re-fetch) if something was
    // actually applied to the listing
    if (hasAppliedFilters) {
      setAppliedFilters({
        statusOptions: [...DEFAULT_STATUS_OPTIONS],
        dateRange: {},
      });
      setPage(1);
    }
  }, [appliedFilters]);

  const setSearch = useCallback(
    (next: string) => {
      if (next.length > 200) {
        return;
      }
      setSearchWithLoad(next);
      setPage(1);
    },
    [setSearchWithLoad]
  );

  // -------------------------------------------------------------------------
  // Fetch
  // -------------------------------------------------------------------------
  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const trimmedSearch = debouncedSearch.trim();
      const selectedStatuses = sanitizeStatusList(
        appliedFilters.statusOptions
          .filter((o) => o.checked)
          .map((o) => o.value)
      );

      const response =
        await listMyServiceRequestsApiService.listMyServiceRequests({
          page,
          limit,
          ...(trimmedSearch && { search: trimmedSearch }),
          ...(selectedStatuses && { status: selectedStatuses }),
          ...(sortOrder !== 'desc' && { sortOrder }),
        });

      const { totalCount: count, totalPages } = response.pagination;

      if (count > 0 && totalPages > 0 && page > totalPages) {
        setPage(totalPages);
        return;
      }

      setItems(response.data);
      setTotalCount(count);
    } catch {
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
      hasFetchedRef.current = true;
    }
  }, [page, limit, debouncedSearch, sortOrder, appliedFilters, setLoading]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  useEffect(() => {
    prevDebouncedSearchRef.current = debouncedSearch;
  }, [debouncedSearch]);

  const hasActiveFilters =
    appliedFilters.statusOptions.some((o) => o.checked) ||
    !!appliedFilters.dateRange.startDate ||
    !!appliedFilters.dateRange.endDate;

  const hasNoData =
    !loading &&
    hasFetchedRef.current &&
    totalCount === 0 &&
    !debouncedSearch.trim() &&
    !hasActiveFilters;

  const isSearchClearing =
    !search.trim() && debouncedSearch.trim() !== '' && !hasActiveFilters;

  const prevDebouncedTrimmed = prevDebouncedSearchRef.current.trim();
  const currentDebouncedTrimmed = debouncedSearch.trim();
  const justClearedSearch =
    prevDebouncedTrimmed !== '' &&
    currentDebouncedTrimmed === '' &&
    !hasActiveFilters;

  return {
    items,
    loading: effectiveLoading,
    page,
    setPage,
    limit,
    totalCount,
    search,
    setSearch,
    sortColumn,
    sortOrder,
    handleSort,
    statusOptions,
    setStatusOptions,
    dateRange,
    setDateRange,
    appliedFilters,
    handleApplyFilters,
    handleClearFilters,
    limitError,
    handleLimitChange,
    hasNoData,
    isSearchClearing: isSearchClearing || justClearedSearch,
  };
}
